import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type PointerEvent,
  type SyntheticEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isCategoryId } from '@dale/plans';

import { ApiError } from '../api/client';
import {
  analyzeFloorPlan,
  getFloorPlan,
  putFloorPlan,
} from '../api/endpoints/floorPlan';
import { createTable, listTables, patchTable } from '../api/endpoints/tables';
import type { BusinessTable, FloorPlanResourceProposal, FloorPlanZoneProposal } from '../api/schemas';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import { ConfirmAction } from '../components/ConfirmAction';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FloorMap } from '../components/FloorMap';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import {
  DEFAULT_MARKER_SIZE,
  ROOM_MARKER_SIZE,
  assignResourcesToZones,
  defaultShapeForKind,
  deriveZoneRegions,
  displayRect,
  freeSpot,
  kindLabel,
  makeFloorPlanTablesReadable,
  normalizeRestaurantFloorPlan,
  pointerToPermille,
  reframeFloorPlan,
  separateFloorPlanResources,
} from '../lib/floorPlan';
import { buildFloorPlanUpload } from '../lib/photoPipeline';
import { modeForCategory } from '../lib/mode';
import { floorCopy } from '../lib/toolCopy';
import { usePresence } from '../lib/motion/hooks';
import { useBusiness } from '../lib/useBusiness';

export const TABLES_QUERY_KEY = ['business-tables'];
export const FLOOR_PLAN_QUERY_KEY = ['business-floor-plan'];

/** Movement below this many permille is a tap, not a drag. */
const TAP_THRESHOLD = 8;

const MODE_KINDS = {
  restaurant: ['table', 'room', 'bar'],
  classes: ['bike', 'mat', 'room'],
  services: ['table', 'room'],
} as const;
type ModeKinds = (typeof MODE_KINDS)[keyof typeof MODE_KINDS];

class AdoptResourceError extends Error {
  constructor(
    readonly resourceName: string,
    readonly remaining: FloorPlanResourceProposal[],
    cause: unknown,
  ) {
    super(errorMessage(cause));
  }
}

export function MesasPage() {
  const business = useBusiness();
  const mode = business ? modeForCategory(business.category) : 'restaurant';
  // Mesas, Salas, Estaciones, Cabinas, Consultorios or Bahías: the trade's own word (Herramientas).
  const words = floorCopy(business && isCategoryId(business.category) ? business.category : undefined);
  const copy = {
    ...words,
    description:
      mode === 'restaurant'
        ? 'El plano de tu salón, mesa por mesa.'
        : mode === 'classes'
          ? 'El plano de tu estudio, puesto por puesto.'
          : 'El plano de tu local: cada reserva cae en su lugar.',
  };
  const kinds: ModeKinds = MODE_KINDS[mode];

  const queryClient = useQueryClient();
  const tablesQuery = useQuery({ queryKey: TABLES_QUERY_KEY, queryFn: () => listTables(false) });
  const planQuery = useQuery({ queryKey: FLOOR_PLAN_QUERY_KEY, queryFn: getFloorPlan });
  const tables = useMemo(() => tablesQuery.data?.data ?? [], [tablesQuery.data]);
  const plan = planQuery.data ?? null;

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const selected = tables.find((table) => table.id === selectedId);

  // The server answers every mutation with the row, so the cache is written
  // directly: an invalidate here would refetch through Hyperdrive's stale
  // window and un-draw the marker that was just placed.
  const upsertCached = (row: BusinessTable) =>
    queryClient.setQueryData<{ data: BusinessTable[] }>(TABLES_QUERY_KEY, (old) => {
      const rest = (old?.data ?? []).filter((table) => table.id !== row.id);
      return { data: row.isActive ? [...rest, row] : rest };
    });

  const moveKey = useRef(createIdempotencyKeyHolder()).current;
  const moveMutation = useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Parameters<typeof patchTable>[1]) =>
      patchTable(id, patch, moveKey.ensure()),
    onSuccess: upsertCached,
    onSettled: () => moveKey.reset(),
  });

  return (
    <main>
      <PageHeader title={copy.title} description={copy.description} />
      {tablesQuery.isLoading || planQuery.isLoading ? <LoadingState label="Cargando el plano…" /> : null}
      {tablesQuery.isError ? <ErrorBanner message={errorMessage(tablesQuery.error)} /> : null}
      {tablesQuery.data && !planQuery.isLoading ? (
        <FloorEditor
          tables={tables}
          plan={plan}
          copy={copy}
          mode={mode}
          kinds={kinds}
          selected={selected}
          onSelect={setSelectedId}
          onMove={(id, x, y, size) =>
            moveMutation.mutate({ id, gridX: x, gridY: y, gridW: size.w, gridH: size.h })
          }
          upsertCached={upsertCached}
        />
      ) : null}
    </main>
  );
}

type Copy = { title: string; description: string; add: string; added: string; empty: string };

function FloorEditor({
  tables,
  plan,
  copy,
  mode,
  kinds,
  selected,
  onSelect,
  onMove,
  upsertCached,
}: {
  tables: BusinessTable[];
  plan: { url: string; width: number; height: number } | null;
  copy: Copy;
  mode: 'restaurant' | 'classes' | 'services';
  kinds: ModeKinds;
  selected: BusinessTable | undefined;
  onSelect: (id: string | undefined) => void;
  onMove: (id: string, x: number, y: number, size: { w: number; h: number }) => void;
  upsertCached: (row: BusinessTable) => void;
}) {
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: string; x: number; y: number; moved: boolean } | undefined>();
  const [proposals, setProposals] = useState<FloorPlanResourceProposal[] | undefined>();
  const [proposalZones, setProposalZones] = useState<FloorPlanZoneProposal[] | undefined>();
  const [saveProgress, setSaveProgress] = useState<{ current: number; total: number } | undefined>();
  const [saveFailure, setSaveFailure] = useState<string | undefined>();
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [confirmingReplacement, setConfirmingReplacement] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const restaurant = mode === 'restaurant';

  const uploadKey = useRef(createIdempotencyKeyHolder()).current;
  const uploadMutation = useMutation({
    mutationFn: async ({ file, replaceExisting }: { file: File; replaceExisting: boolean }) => {
      const variant = await buildFloorPlanUpload(file);
      const row = await putFloorPlan(variant, uploadKey.ensure());
      const removedIds: string[] = [];
      const failedNames: string[] = [];

      // The image must land first. A cancelled picker or failed upload can
      // therefore never erase the owner's current seating map.
      if (replaceExisting) {
        for (const table of tables) {
          const removeKey = createIdempotencyKeyHolder();
          try {
            await patchTable(table.id, { isActive: false }, removeKey.ensure());
            removedIds.push(table.id);
          } catch {
            failedNames.push(table.name);
          }
        }
      }

      return { row, removedIds, failedNames };
    },
    onSuccess: ({ row, removedIds, failedNames }) => {
      queryClient.setQueryData(FLOOR_PLAN_QUERY_KEY, row);
      if (removedIds.length > 0) {
        const removed = new Set(removedIds);
        queryClient.setQueryData<{ data: BusinessTable[] }>(TABLES_QUERY_KEY, (old) => ({
          data: (old?.data ?? []).filter((table) => !removed.has(table.id)),
        }));
      }
      onSelect(undefined);
      setProposals(undefined);
      setProposalZones(undefined);
      reconciliationTargets.current = undefined;
      setSaveFailure(undefined);
      setUploadError(
        failedNames.length > 0
          ? `El plano nuevo está listo, pero no pudimos quitar: ${failedNames.join(', ')}.`
          : undefined,
      );
    },
    onError: (error) => setUploadError(errorMessage(error)),
    onSettled: () => uploadKey.reset(),
  });

  const analyzeMutation = useMutation({
    mutationFn: analyzeFloorPlan,
    onSuccess: (result) => {
      setUploadError(undefined);
      setSaveFailure(undefined);
      reconciliationTargets.current = undefined;
      const openZones = result.zones.filter((zone) => !zone.isPrivate);
      const assigned = assignResourcesToZones(result.resources, openZones);
      if (restaurant) {
        const normalized = normalizeRestaurantFloorPlan(assigned, openZones);
        setProposals(normalized.resources);
        setProposalZones(normalized.zones.map((zone) => ({ ...zone, isPrivate: false })));
      } else {
        const reframed = reframeFloorPlan(assigned, openZones);
        setProposals(separateFloorPlanResources(makeFloorPlanTablesReadable(reframed.resources)));
        setProposalZones(reframed.zones);
      }
    },
    onError: (error) =>
      setUploadError(
        error instanceof ApiError && error.status === 503
          ? 'La detección automática no está disponible ahora. Agrega los lugares manualmente.'
          : errorMessage(error),
      ),
  });

  const adoptKey = useRef(createIdempotencyKeyHolder()).current;
  // A partial Apply retries only its failed suffix. Retaining the accepted
  // proposal lets that retry keep earlier successful rows while it finishes
  // reconciling the same complete plan.
  const reconciliationTargets = useRef<FloorPlanResourceProposal[] | undefined>(undefined);
  const adoptMutation = useMutation({
    mutationFn: async (ghosts: FloorPlanResourceProposal[]) => {
      const allTables = (await listTables(true)).data;
      const targetGhosts = reconciliationTargets.current ?? ghosts;
      reconciliationTargets.current = targetGhosts;
      const sameResource = (table: BusinessTable, ghost: FloorPlanResourceProposal) =>
        table.kind === ghost.kind &&
        table.name.trim().toLocaleLowerCase('es-PA') === ghost.name.trim().toLocaleLowerCase('es-PA');
      const retainedIds = new Set<string>();

      // A detected proposal is a full replacement of the saved plan. Preserve
      // exactly one active row for every matching proposed resource; all old
      // tables, legacy kinds, and duplicate architecture are soft-deactivated
      // so existing reservations and history continue to point at their rows.
      for (const ghost of targetGhosts) {
        const retained = allTables.find(
          (table) => table.isActive && !retainedIds.has(table.id) && sameResource(table, ghost),
        );
        if (retained) retainedIds.add(retained.id);
      }
      for (let index = 0; index < allTables.length; index += 1) {
        const table = allTables[index]!;
        if (!table.isActive || retainedIds.has(table.id)) continue;
        try {
          const removed = await patchTable(table.id, { isActive: false }, adoptKey.ensure());
          upsertCached(removed);
          allTables[index] = removed;
        } catch (error) {
          throw new AdoptResourceError(table.name, ghosts, error);
        } finally {
          adoptKey.reset();
        }
      }
      const total = ghosts.length;
      setSaveFailure(undefined);
      for (let index = 0; index < ghosts.length; index += 1) {
        const ghost = ghosts[index]!;
        setSaveProgress({ current: index + 1, total });
        const geometry = {
          name: ghost.name,
          seats: ghost.seats,
          kind: ghost.kind,
          gridX: ghost.x,
          gridY: ghost.y,
          gridW: ghost.w,
          gridH: ghost.h,
          shape: ghost.shape,
          rotation: ghost.rotation,
          isPrivate: ghost.isPrivate,
          ...(ghost.zone ? { zone: ghost.zone } : {}),
        };
        const existing =
          allTables.find((table) => table.isActive && sameResource(table, ghost)) ??
          allTables.find((table) => sameResource(table, ghost));
        try {
          const created = existing
            ? await patchTable(existing.id, { ...geometry, isActive: true }, adoptKey.ensure())
            : await createTable(geometry, adoptKey.ensure());
          upsertCached(created);
          const knownIndex = allTables.findIndex((table) => table.id === created.id);
          if (knownIndex >= 0) allTables[knownIndex] = created;
          else allTables.push(created);
        } catch (error) {
          throw new AdoptResourceError(ghost.name, ghosts.slice(index), error);
        } finally {
          adoptKey.reset();
        }
      }
    },
    onSuccess: () => {
      setProposals(undefined);
      setProposalZones(undefined);
      reconciliationTargets.current = undefined;
      setSaveProgress(undefined);
      setSaveFailure(undefined);
    },
    onError: (error) => {
      setSaveProgress(undefined);
      if (error instanceof AdoptResourceError) {
        setProposals(error.remaining);
        setSaveFailure(`No pudimos guardar ${error.resourceName}. ${error.message}`);
      } else {
        setSaveFailure(errorMessage(error));
      }
    },
  });

  const visibleZones = proposals
    ? proposalZones ?? []
    : deriveZoneRegions(tables);

  function markerPointerDown(event: PointerEvent<HTMLButtonElement>, table: BusinessTable) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const rect = displayRect(table);
    setDrag({ id: table.id, x: rect.gridX, y: rect.gridY, moved: false });
  }

  function markerPointerMove(event: PointerEvent<HTMLButtonElement>, table: BusinessTable) {
    if (!drag || drag.id !== table.id || !canvasRef.current) return;
    const rect = displayRect(table);
    const canvas = canvasRef.current.getBoundingClientRect();
    const spot = pointerToPermille(canvas, event.clientX, event.clientY, rect.gridW, rect.gridH);
    const moved =
      drag.moved ||
      Math.abs(spot.x - displayRect(table).gridX) > TAP_THRESHOLD ||
      Math.abs(spot.y - displayRect(table).gridY) > TAP_THRESHOLD;
    setDrag({ id: table.id, x: spot.x, y: spot.y, moved });
  }

  function markerPointerUp(table: BusinessTable) {
    if (!drag || drag.id !== table.id) return;
    const rect = displayRect(table);
    if (drag.moved) {
      onMove(table.id, drag.x, drag.y, { w: rect.gridW, h: rect.gridH });
    } else {
      onSelect(table.id === selected?.id ? undefined : table.id);
    }
    setDrag(undefined);
  }

  return (
    <>
      <section aria-label="Plano" className="section">
        <div className="plan-toolbar">
          <button
            type="button"
            className={plan ? 'btn secondary' : 'btn'}
            disabled={uploadMutation.isPending || adoptMutation.isPending}
            onClick={() => {
              if (plan && tables.length > 0) setConfirmingReplacement(true);
              else fileInputRef.current?.click();
            }}
          >
            {uploadMutation.isPending ? 'Subiendo…' : plan ? 'Cambiar plano' : 'Subir plano'}
          </button>
          <input
            ref={fileInputRef}
            className="plan-file-input"
            type="file"
            accept="image/*"
            aria-label="Seleccionar nueva imagen del plano"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadMutation.mutate({ file, replaceExisting: tables.length > 0 });
              event.target.value = '';
            }}
          />
          {plan ? (
            <button
              type="button"
              className="btn"
              disabled={analyzeMutation.isPending}
              onClick={() => analyzeMutation.mutate()}
            >
              {analyzeMutation.isPending
                ? 'Detectando…'
                : restaurant
                  ? 'Detectar mesas'
                  : 'Detectar lugares'}
            </button>
          ) : null}
        </div>
        {uploadError ? <ErrorBanner message={uploadError} /> : null}

        <FloorMap
          canvasRef={canvasRef}
          plan={plan}
          markers={proposals ? [] : tables}
          ghosts={proposals}
          zones={visibleZones}
          showSourceImage={tables.length === 0 && proposals === undefined}
          emptyState={
            tables.length === 0 && !proposals?.length && !plan ? (
              <div className="plan-empty">
                <EmptyState title={copy.empty} description="Sube el plano o agrega el primero a mano." />
              </div>
            ) : null
          }
          dragPosition={drag}
          isSelected={(table) => table.id === selected?.id}
          markerDataAttrs={(table) => ({ 'data-private': table.isPrivate || undefined })}
          onMarkerPointerDown={markerPointerDown}
          onMarkerPointerMove={markerPointerMove}
          onMarkerPointerUp={markerPointerUp}
          onMarkerPointerCancel={() => setDrag(undefined)}
        />

        {proposals ? (
          <div className="row plan-proposals">
            <p className="row-sub grow">
              {proposals.length === 0
                ? restaurant
                  ? 'No encontramos mesas ni salones privados en el plano.'
                  : 'No encontramos lugares en el plano.'
                : restaurant
                  ? proposalSummary(proposals)
                  : `Encontramos ${proposals.length} ${proposals.length === 1 ? 'lugar' : 'lugares'}. Revisa y ajusta después.`}
            </p>
            {proposals.length > 0 ? (
              <button
                type="button"
                className="btn"
                disabled={adoptMutation.isPending}
                onClick={() => adoptMutation.mutate(proposals)}
              >
                {adoptMutation.isPending && saveProgress
                  ? `Guardando ${saveProgress.current} de ${saveProgress.total}`
                  : saveFailure
                    ? `Reintentar ${proposals.length}`
                    : `Aplicar ${proposals.length}`}
              </button>
            ) : null}
            <button
              type="button"
              className="btn secondary"
              disabled={adoptMutation.isPending}
              onClick={() => {
                setProposals(undefined);
                setProposalZones(undefined);
                reconciliationTargets.current = undefined;
                setSaveFailure(undefined);
              }}
            >
              Descartar
            </button>
          </div>
        ) : tables.length > 0 ? (
          <p className="row-sub plan-hint">
            Arrastra un lugar para moverlo; tócalo para editarlo.
          </p>
        ) : null}

        <div aria-live="polite" aria-atomic="true">
          {saveFailure ? <ErrorBanner message={saveFailure} /> : null}
          {adoptMutation.isPending && saveProgress ? (
            <p className="row-sub">Guardando {saveProgress.current} de {saveProgress.total}</p>
          ) : null}
        </div>

        {selected ? (
          <MarkerSheet
            key={selected.id}
            table={selected}
            kinds={kinds}
            onDone={(row) => {
              if (row) upsertCached(row);
              onSelect(undefined);
            }}
          />
        ) : null}
      </section>

      <PlanReplacementDialog
        open={confirmingReplacement}
        resourceCount={tables.length}
        onCancel={() => setConfirmingReplacement(false)}
        onConfirm={() => {
          setConfirmingReplacement(false);
          fileInputRef.current?.click();
        }}
      />

      <AddMarkerForm copy={copy} kinds={kinds} tables={tables} upsertCached={upsertCached} />
    </>
  );
}

function PlanReplacementDialog({
  open,
  resourceCount,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  resourceCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { mounted, phase } = usePresence(open, { exitMs: 150 });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (phase === 'opening' && !dialog.open) dialog.showModal();
    else if (phase === 'closed' && dialog.open) dialog.close();
  }, [phase]);

  function handleBackdropClick(event: MouseEvent<HTMLDialogElement>) {
    if (event.target === ref.current) onCancel();
  }

  // Esc leaves like Cancelar, shrinking back instead of vanishing.
  function handleCancel(event: SyntheticEvent<HTMLDialogElement>) {
    event.preventDefault();
    onCancel();
  }

  return (
    <dialog
      ref={ref}
      className="plan-replace-dialog"
      data-phase={phase}
      inert={phase === 'closing'}
      aria-hidden={phase === 'closing' || undefined}
      aria-labelledby="plan-replace-title"
      onCancel={handleCancel}
      onClose={onCancel}
      onClick={handleBackdropClick}
    >
      {mounted ? (
        <div className="plan-replace-body">
          <h2 id="plan-replace-title" className="plan-replace-title">¿Cambiar el plano?</h2>
          <p>
            Esto quitará {resourceCount === 1 ? 'el lugar existente' : `los ${resourceCount} lugares existentes`}
            {' '}cuando el plano nuevo termine de subir. Las reservas y el historial no se borrarán.
          </p>
          <div className="row plan-replace-actions">
            <button type="button" className="btn danger" onClick={onConfirm}>
              Sí, cambiar plano
            </button>
            <button type="button" className="btn secondary" onClick={onCancel}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </dialog>
  );
}

function proposalSummary(proposals: FloorPlanResourceProposal[]): string {
  const tables = proposals.filter((proposal) => proposal.kind === 'table').length;
  const rooms = proposals.filter((proposal) => proposal.kind === 'room').length;
  const bars = proposals.filter((proposal) => proposal.kind === 'bar').length;
  const parts = [
    tables > 0 ? `${tables} ${tables === 1 ? 'mesa' : 'mesas'}` : undefined,
    rooms > 0 ? `${rooms} ${rooms === 1 ? 'salón privado' : 'salones privados'}` : undefined,
    bars > 0 ? `${bars} ${bars === 1 ? 'bar' : 'bares'}` : undefined,
  ].filter(Boolean);
  return `Encontramos ${parts.join(' y ')}. Revisa las formas antes de aplicar.`;
}

/** Edit panel for the selected marker: identity, capacity, and the private toggle. */
function MarkerSheet({
  table,
  kinds,
  onDone,
}: {
  table: BusinessTable;
  kinds: ModeKinds;
  onDone: (row?: BusinessTable) => void;
}) {
  const [name, setName] = useState(table.name);
  const [seats, setSeats] = useState(String(table.seats));
  const [zone, setZone] = useState(table.zone);
  const [kind, setKind] = useState(table.kind);
  const [shape, setShape] = useState(table.shape);
  const [isPrivate, setIsPrivate] = useState(table.isPrivate);
  const [error, setError] = useState<string | undefined>();

  const patchKey = useRef(createIdempotencyKeyHolder()).current;
  const saveMutation = useMutation({
    mutationFn: () =>
      patchTable(
        table.id,
        { name: name.trim(), seats: Number(seats), zone, kind, shape, isPrivate },
        patchKey.ensure(),
      ),
    meta: { exito: 'Cambios guardados' },
    onSuccess: (row) => onDone(row),
    onError: (mutationError) => setError(errorMessage(mutationError)),
    onSettled: () => patchKey.reset(),
  });
  const removeKey = useRef(createIdempotencyKeyHolder()).current;
  const removeMutation = useMutation({
    mutationFn: () => patchTable(table.id, { isActive: false }, removeKey.ensure()),
    meta: { exito: 'Quitado del plano' },
    onSuccess: (row) => onDone(row),
    onSettled: () => removeKey.reset(),
  });

  return (
    <form
      className="marker-sheet"
      aria-label={`Editar ${table.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() === '' || Number(seats) < 1) return;
        saveMutation.mutate();
      }}
    >
      <div className="form-grid">
        <FormField label="Nombre" htmlFor="marker-nombre">
          <input id="marker-nombre" value={name} onChange={(event) => setName(event.target.value)} required />
        </FormField>
        <FormField label="Personas" htmlFor="marker-personas">
          <input
            id="marker-personas"
            type="number"
            min={1}
            max={100}
            value={seats}
            onChange={(event) => setSeats(event.target.value)}
            required
          />
        </FormField>
        <FormField label="Tipo" htmlFor="marker-tipo">
          <select id="marker-tipo" value={kind} onChange={(event) => setKind(event.target.value)}>
            {kinds.map((option) => (
              <option key={option} value={option}>
                {kindLabel(option)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Zona" htmlFor="marker-zona">
          <input id="marker-zona" value={zone} onChange={(event) => setZone(event.target.value)} />
        </FormField>
        <FormField label="Forma" htmlFor="marker-forma">
          <select id="marker-forma" value={shape} onChange={(event) => setShape(event.target.value as BusinessTable['shape'])}>
            <option value="circle">Redonda</option>
            <option value="square">Cuadrada</option>
            <option value="rectangle">Rectangular</option>
          </select>
        </FormField>
      </div>
      <label className="marker-private">
        <input
          type="checkbox"
          checked={isPrivate}
          onChange={(event) => setIsPrivate(event.target.checked)}
        />
        <span>
          Solo a pedido
          <span className="row-sub marker-private-sub">
            Nunca se asigna solo; el cliente tiene que pedirlo.
          </span>
        </span>
      </label>
      {error ? <ErrorBanner message={error} /> : null}
      <div className="row marker-sheet-actions">
        <button type="submit" className="btn" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" className="btn secondary" onClick={() => onDone()}>
          Cancelar
        </button>
        <span className="grow" />
        <ConfirmAction
          label="Quitar del plano"
          variant="secondary"
          onConfirm={() => removeMutation.mutate()}
          pending={removeMutation.isPending}
        />
      </div>
    </form>
  );
}

function AddMarkerForm({
  copy,
  kinds,
  tables,
  upsertCached,
}: {
  copy: Copy;
  kinds: ModeKinds;
  tables: BusinessTable[];
  upsertCached: (row: BusinessTable) => void;
}) {
  const [name, setName] = useState('');
  const [seats, setSeats] = useState(kinds[0] === 'table' ? '4' : '1');
  const [kind, setKind] = useState<string>(kinds[0]);
  const [shape, setShape] = useState(defaultShapeForKind(kinds[0]));
  const [formError, setFormError] = useState<string | undefined>();

  const createKey = useRef(createIdempotencyKeyHolder()).current;
  const createMutation = useMutation({
    mutationFn: () => {
      const spot = freeSpot(tables);
      const size = kind === 'room' ? ROOM_MARKER_SIZE : DEFAULT_MARKER_SIZE;
      return createTable(
        {
          name: name.trim(),
          seats: Number(seats),
          kind,
          gridX: spot.x,
          gridY: spot.y,
          gridW: size,
          gridH: size,
          shape,
          rotation: 0,
        },
        createKey.ensure(),
      );
    },
    meta: { exito: copy.added },
    onSuccess: (created) => {
      setName('');
      setFormError(undefined);
      upsertCached(created);
    },
    onError: (error) => setFormError(errorMessage(error)),
    onSettled: () => createKey.reset(),
  });

  function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (name.trim() === '' || Number(seats) < 1) return;
    createMutation.mutate();
  }

  return (
    <section aria-label={copy.add} className="section">
      <h2 className="section-label">{copy.add}</h2>
      <form onSubmit={handleCreate} className="form-grid">
        <FormField label="Nombre" htmlFor="nuevo-nombre">
          <input
            id="nuevo-nombre"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={`${kindLabel(kinds[0])} 1`}
            required
          />
        </FormField>
        <FormField label="Personas" htmlFor="nuevo-personas">
          <input
            id="nuevo-personas"
            type="number"
            min={1}
            max={100}
            value={seats}
            onChange={(event) => setSeats(event.target.value)}
            required
          />
        </FormField>
        <FormField label="Tipo" htmlFor="nuevo-tipo">
          <select
            id="nuevo-tipo"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value);
              setShape(defaultShapeForKind(event.target.value));
            }}
          >
            {kinds.map((option) => (
              <option key={option} value={option}>
                {kindLabel(option)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Forma" htmlFor="nuevo-forma">
          <select
            id="nuevo-forma"
            value={shape}
            onChange={(event) => setShape(event.target.value as BusinessTable['shape'])}
          >
            <option value="circle">Redonda</option>
            <option value="square">Cuadrada</option>
            <option value="rectangle">Rectangular</option>
          </select>
        </FormField>
        {formError ? <ErrorBanner message={formError} /> : null}
        <div>
          <button type="submit" className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Guardando…' : copy.add}
          </button>
        </div>
      </form>
    </section>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
